import { restoreErrorMessage } from "@/api/archived";
import {
  useAgentCapabilities,
  useHarnessCapabilities,
} from "@/api/harnessCapabilities";
import { useUpdateHarnessPreferences } from "@/api/harnessPreferences";
import {
  TokenMeter,
  UsageRing,
  useUsage,
} from "@/components/billing/TokenMeter";
import { AgentRowLabel, ModelRowLabel } from "@/components/harness/agentRows";
import { useAgentConnect } from "@/components/harness/useAgentConnect";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DS_MENU_CONTENT_CLASS,
  DS_MENU_ITEM_CLASS,
  DS_MENU_SEPARATOR_CLASS,
} from "@/components/ui/dsMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AGENT_IDS,
  AGENT_META,
  AGENT_ORDER,
  AgentCapability,
  agentHarnessId,
  agentOf,
  findAgent,
} from "@/constants/agents";
import { harnessSeatVisible } from "@/constants/harnesses";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useHarnessDefault } from "@/hooks/useHarnessDraft";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { cn } from "@/utils";
import { formatInteger } from "@/utils/currency";
import { format } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Check, ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";
import toast from "react-hot-toast";

/**
 * The footer's usage ring with the default menu behind it (DS decision 045). The menu sets the
 * agent and model every NEW chat starts on — the user's harness preference, which every
 * session-less composer seeds its draft from; a pick in a composer is that surface's alone.
 *
 * The rows are the composer's agents, drawn by the same row as "Where this chat runs": Cohesive a
 * plain row (its tier picks the model, so it names none); a connected subscription a nested menu
 * of its aliases, then all its models; a subscription not yet connected only offers Connect —
 * the server refuses it as a default until then. The menu stays open through the connect
 * dialog, so the row becomes selectable in place once the catalog refreshes. Then one usage row,
 * the way to Usage.
 *
 * Shown on the composer's rule — the harness-runtime flag on and the catalog loaded. Otherwise
 * the ring is the plain token meter it was, a click opening Usage. Usage stays hidden where it
 * is today (the real iOS container, an account without billing data); the ring then renders
 * empty and the menu carries the default alone.
 */
export function ModelMenu() {
  const flags = useFlags<Flags>();
  const { data: capabilities } = useHarnessCapabilities();
  const { data: agents } = useAgentCapabilities();
  const harnessDefault = useHarnessDefault();
  const usage = useUsage();
  const lg = useBreakpoint("lg");
  const openSettings = useOpenSettings();
  const updatePreferences = useUpdateHarnessPreferences();
  const agentConnect = useAgentConnect();
  const [open, setOpen] = useState(false);

  if (
    !harnessSeatVisible(undefined, !!flags.harnessRuntime, capabilities) ||
    !agents?.length ||
    !harnessDefault
  ) {
    return <TokenMeter />;
  }

  const defaultAgent = agentOf(harnessDefault.harness);
  const defaultCapability = findAgent(agents, defaultAgent);
  const modelLabel = defaultCapability?.models?.find(
    (m) => m.id === harnessDefault.model,
  )?.label;
  const label = `Model: ${AGENT_META[defaultAgent].label}${modelLabel ? `, ${modelLabel}` : ""}`;
  const title = usage ? `Usage: ${usage.pct}% · ${label}` : label;

  const pick = (agent: AgentCapability, model?: string) => {
    if (agent === defaultCapability && model === harnessDefault.model) return;
    updatePreferences.mutate(
      // Cohesive is stored as cohesive, so the default follows the owner's harness-runtime flag.
      { default_harness: agentHarnessId(agent), default_model: model ?? null },
      {
        onError: (err) =>
          toast.error(restoreErrorMessage(err, "Failed to set the default")),
      },
    );
  };

  const modelItem = (
    agent: AgentCapability,
    key: string,
    modelId: string,
    content: ReactNode,
  ) => (
    <DropdownMenuItem
      key={key}
      className={cn(DS_MENU_ITEM_CLASS, "py-1")}
      onSelect={() => pick(agent, modelId)}
    >
      {content}
      {agent === defaultCapability && modelId === harnessDefault.model && (
        <Check className="text-n-90" />
      )}
    </DropdownMenuItem>
  );

  // Non-modal like the account quick-nav beside it: the usage row opens the
  // Manage modal, and a modal menu's body lock outlives it.
  return (
    <>
      <DropdownMenu
        modal={false}
        open={open}
        // Held open under the connect dialog, whose clicks land outside the menu.
        onOpenChange={(next) => setOpen(next || !!agentConnect.dialog)}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={title}
                data-onboarding-target="footer-usage"
                // The held-open wash keys off aria-expanded: the tooltip trigger
                // writes data-state onto the same button and clobbers the menu's.
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-md outline-none transition-colors hover:bg-n-05 aria-expanded:bg-n-10 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
              >
                <UsageRing pct={usage?.pct ?? 0} />
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" align="end">
            {title}
          </TooltipContent>
        </Tooltip>
        {/* On lg the menu flies out right of the pane edge, bottom-aligned, like
            the account quick-nav; below lg the rail is a drawer and it opens up. */}
        <DropdownMenuContent
          side={lg ? "right" : "top"}
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className={cn("w-60", DS_MENU_CONTENT_CLASS)}
        >
          <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-n-50">
            Default for new chats
          </DropdownMenuLabel>
          {AGENT_ORDER.map((id) => {
            const agent = findAgent(agents, id);
            if (!agent) return null;
            const row = (
              <AgentRowLabel
                agent={id}
                capability={agent}
                onConnect={() => agentConnect.openConnect(id)}
              />
            );
            const check = agent === defaultCapability && (
              <Check className="text-n-90" />
            );
            if (agent.id === AGENT_IDS.COHESIVE) {
              return (
                <DropdownMenuItem
                  key={id}
                  className={cn(DS_MENU_ITEM_CLASS, "py-1")}
                  onSelect={() => pick(agent)}
                >
                  {row}
                  {check}
                </DropdownMenuItem>
              );
            }
            if (!agent.connected) {
              // Not a default until connected: the row does nothing, its Connect button opens
              // the dialog.
              return (
                <DropdownMenuItem
                  key={id}
                  className={cn(DS_MENU_ITEM_CLASS, "cursor-default py-1")}
                  aria-disabled
                  onSelect={(event) => event.preventDefault()}
                >
                  {row}
                </DropdownMenuItem>
              );
            }
            const aliasModels = new Set(agent.aliases?.map((a) => a.model_id));
            const models = agent.models ?? [];
            const hasMoreModels = models.some((m) => !aliasModels.has(m.id));
            return (
              <DropdownMenuSub key={id}>
                <DropdownMenuSubTrigger
                  className={cn(
                    DS_MENU_ITEM_CLASS,
                    "py-1 data-[state=open]:bg-n-05",
                  )}
                >
                  {row}
                  {check}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent
                  className={cn("w-64", DS_MENU_CONTENT_CLASS)}
                >
                  {(agent.aliases ?? []).map((alias) =>
                    modelItem(
                      agent,
                      alias.id,
                      alias.model_id,
                      <ModelRowLabel
                        label={alias.label}
                        description={alias.description}
                      />,
                    ),
                  )}
                  {(hasMoreModels || !agent.aliases?.length) && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger
                        className={cn(
                          DS_MENU_ITEM_CLASS,
                          "text-n-60 data-[state=open]:bg-n-05",
                        )}
                      >
                        More models
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        className={cn("w-60", DS_MENU_CONTENT_CLASS)}
                      >
                        {models.map((model) =>
                          modelItem(
                            agent,
                            model.id,
                            model.id,
                            <ModelRowLabel label={model.label} />,
                          ),
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            );
          })}
          {usage && (
            <>
              <DropdownMenuSeparator className={DS_MENU_SEPARATOR_CLASS} />
              <DropdownMenuItem
                className={DS_MENU_ITEM_CLASS}
                onSelect={() => openSettings(ROUTES.SETTINGS.USAGE)}
              >
                <UsageRing pct={usage.pct} size={14} stroke={2} />
                <span className="flex min-w-0 flex-1 flex-col py-1">
                  <span className="truncate">
                    {usage.pct}% of {formatInteger(usage.total)} credits
                  </span>
                  {usage.resetsAt && (
                    <span className="text-xs text-n-50">
                      Resets {format(usage.resetsAt, "MMM d")}
                    </span>
                  )}
                </span>
                <ChevronRight className="text-n-40" />
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {agentConnect.dialog}
    </>
  );
}
